import React, { useState } from "react";

import {
    CancelOutlined,
    Close
} from "@mui/icons-material";

import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    TextField
} from "@mui/material";

import AxiosInstance from "../../services/AxiosInstance";
import { useAlert } from "../alerts/AlertContext";
import { CHANGE_REQUEST_URL } from "../../Constants/Constants";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";

const CancelChangeDialog = ({
    open,
    change,
    onClose,
    onSuccess
}) => {

    const showAlert = useAlert();
    const [reason, setReason] = useState("");
    const [cancelling, setCancelling] =
        useState(false);


    /*
     * Cancel change
     */
    const handleCancel = async () => {

        if (!change?.id) {
            return;
        }

        if (!reason.trim()) {
            return;
        }

        setCancelling(true);

        try {

            const response =
                await AxiosInstance.post(
                    `${CHANGE_REQUEST_URL}/${change.id}/cancel`,
                    {
                        reason: reason
                    }
                );


            showAlert(
                "Change cancelled successfully",
                "success"
            );


            onSuccess?.(
                response.data
            );

        } catch (error) {

            console.error(
                "Unable to cancel change",
                error
            );

            showAlert(
                error?.response?.data?.message ||
                "Unable to cancel change",
                "error"
            );

        } finally {

            setCancelling(false);

        }
    };


    /*
     * Close
     */
    const handleClose = () => {

        if (cancelling) {
            return;
        }

        onClose?.();

    };


    return (

        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >

                <Typography
                    variant="h6"
                    fontWeight={600}
                >
                    Cancel Change
                </Typography>


                <IconButton
                    onClick={handleClose}
                    disabled={cancelling}
                >
                    <Close />
                </IconButton>

            </DialogTitle>


            <DialogContent dividers>

                <Stack spacing={2}>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2,
                            borderRadius: 1,
                            backgroundColor:
                                "error.lighter"
                        }}
                    >

                        <CancelOutlined
                            color="error"
                        />

                        <Typography
                            variant="body2"
                            fontWeight={500}
                        >
                            You are about to cancel this
                            change request.
                        </Typography>

                    </Box>
                    <ThemeProvider>
                        <TextField
                            fullWidth
                            required
                            multiline
                            minRows={4}
                            label="Cancellation Reason"
                            placeholder="Enter the reason for cancelling this change"
                            value={reason}
                            onChange={(e) =>
                                setReason(e.target.value)
                            }
                            disabled={cancelling}
                        />
                    </ThemeProvider>

                    <Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Change
                        </Typography>

                        <Typography
                            variant="body1"
                            fontWeight={600}
                        >
                            {change?.changeNumber || "-"}
                        </Typography>

                    </Box>


                    <Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Title
                        </Typography>

                        <Typography
                            variant="body2"
                        >
                            {change?.title || "-"}
                        </Typography>

                    </Box>


                    <Box>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Current Status
                        </Typography>

                        <Typography
                            variant="body2"
                        >
                            {change?.statusName || "-"}
                        </Typography>

                    </Box>


                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Are you sure you want to cancel this
                        change? This action will mark the
                        change request as cancelled.
                    </Typography>

                </Stack>

            </DialogContent>


            <DialogActions
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Button
                    onClick={handleClose}
                    disabled={cancelling}
                >
                    Keep Change
                </Button>


                <Button
                    variant="contained"
                    color="error"
                    onClick={handleCancel}
                    disabled={cancelling || !reason.trim()}
                    startIcon={
                        cancelling
                            ? (
                                <CircularProgress
                                    size={18}
                                />
                            )
                            : (
                                <CancelOutlined />
                            )
                    }
                >
                    {cancelling
                        ? "Cancelling..."
                        : "Cancel Change"
                    }
                </Button>

            </DialogActions>

        </Dialog>
    );
};


export default CancelChangeDialog;